/**
 * PATH: packages/ui/src/kanban/components/EmailFromCard.tsx
 * FILE_ID: UI-KANBAN-EMAIL-FROMCARD-001
 * PURPOSE: Render an email-shaped read-only view for a Kanban Card
 *          whose `primary_email` is NULL but whose title + summary
 *          (or Trello comments) clearly originated as a forwarded
 *          email (the Trello / BenkoBoard import case).
 * WHY:
 *   Operator complaint (2026-05-11, Newmark BenkoDesk board):
 *     "I opened benko board and these are email cards I can't
 *      click them ... look I see the description it should be a
 *      fucking email with tab to switch to card."
 *
 *   Operator complaint follow-up (2026-05-20): "you did not
 *   backfill and allocate emails properly from the mailbox here".
 *   On Newmark cards like "Pay May Officeworks CC?" the title is
 *   NOT email-shaped (it's an operator-chosen subject) so the v2
 *   title+summary parse fell back to the empty placeholder. BUT
 *   the real forwarded-email content is sitting RIGHT THERE on the
 *   card's TrelloAction(type='commentCard') rows — that's how
 *   Trello delivers the email body when the original is forwarded
 *   into the list. The backend already exposes those rows via
 *   `GET /api/trello/cards/<external_id>/` (used by the Card-tab's
 *   `<TrelloCardDetailSection>`); we just weren't reading them on
 *   the Email tab.
 *
 *   v3 reuses the SAME TanStack cache key as that Card-tab fetch so
 *   there's no extra round-trip, parses each comment with the SAME
 *   regex the backend linker uses (`COMMENT_FROM_RE`, mirrored in
 *   `./email-from-card-parse.ts`), and renders the parsed comments
 *   as a real email thread above the v2 title fallback. Result:
 *   cards whose `primary_email` link is still pending now SHOW the
 *   forwarded message body inline.
 *
 * FLOW (v3):
 *   ┌─────────────────┐  ┌─────────────────────┐  ┌────────────────┐
 *   │ Trello comments │→ │ parseEmailThreadFro │→ │ thread items   │
 *   │  (from API)     │  │ mComments()         │  │ {from,body,…}  │
 *   └─────────────────┘  └─────────────────────┘  └────────────────┘
 *                                                          │
 *   ┌─────────────┐    ┌──────────────────┐                ▼
 *   │ card.title  │ →  │ parseEmailTitle  │ →  ┌──────────────────┐
 *   │ card.summary│    │ stripBenkoCmds   │ →  │ Render preferring│
 *   └─────────────┘    └──────────────────┘    │ thread, else v2  │
 *                                              └──────────────────┘
 *
 * DEPENDENCIES:
 *   - ./email-from-card-parse: all pure I→O helpers (regex + parse)
 *   - @tanstack/react-query: shared cache with TrelloCardDetailSection
 *
 * ARC-1 CONTRACT DECISION (2026-07-03): the spec's "delete the
 *   kanban-local rendering fork once parity is verified" was resolved
 *   with prod evidence, not deletion. Census (org 6): 845 ACTIVE cards
 *   have NO Email row (primary_email NULL, related_emails empty) and
 *   render this view; their content exists ONLY as Trello imports, and
 *   a linker dry-run (deterministic pass, 2,286 cards) matched 0 —
 *   the originals were never synced into a mailbox. Parity with the
 *   email-hub reader is therefore impossible for this data class:
 *   this is a Trello-import viewer, not a fork of email rendering.
 *   Everything that CAN converge did: real emails render via the
 *   shared reader/composer/tag pipeline (ARC-1 MR1–MR5), and the
 *   drawer now offers in-place search+link next to this view so each
 *   card flips to the shared reader the moment its inbox email is
 *   linked. Do NOT delete while the census population is non-zero.
 * RELATED:
 *   - UI-KANBAN-EMAIL-FROMCARD-PARSE-001 (sibling pure module)
 *   - CardDetailDrawer.tsx (the host that mounts this component)
 *   - TrelloCardDetailSection.tsx (sibling Card-tab consumer of the
 *     same TanStack query key)
 *   - email_kanban/services/trello_card_email_linker.py (backend
 *     linker whose regex this file MIRRORS — see parse module docs)
 */
import { useQuery } from '@tanstack/react-query'
import { ExternalLink, Mail } from 'lucide-react'

import type { TrelloCardDetail } from '../types'

import {
  extractWebmailLink,
  isTrelloMirrorCard,
  parseEmailThreadFromComments,
  parseEmailTitle,
  stripBenkoCommandPreamble,
  stripWebmailPreamble,
} from './email-from-card-parse'

// Re-export pure helpers + types from the parse module so existing
// importers (tests, drawer, app barrel) don't need to change paths.
// This is the back-compat seam — keeping it forever has a cost
// (two import paths for the same symbol), but for THIS module the
// public surface is small (5 helpers) and external callers shouldn't
// be reaching into the parse file directly anyway.
export {
  cardLooksLikeEmail,
  extractWebmailLink,
  isTrelloMirrorCard,
  parseEmailFromTrelloComment,
  parseEmailThreadFromComments,
  parseEmailTitle,
  stripBenkoCommandPreamble,
  stripWebmailPreamble,
} from './email-from-card-parse'
export type { EmailThreadItem, WebmailLink } from './email-from-card-parse'

export interface EmailFromCardProps {
  /** The kanban card whose email-shaped content we render. Caller
   *  is expected to have already checked `cardLooksLikeEmail(card)`
   *  before mounting this component. */
  card: {
    id: string
    title: string
    summary: string
    primary_email: string | null
    updated_at?: string | null
    created_at?: string | null
    /** Provenance. Trello comment fetch is gated on `trello`. */
    provider?: string | null
    /** When set AND `provider === 'trello'`, the component fetches
     *  Trello card detail and renders the comments as an email
     *  thread. Job-pipeline ids (`{board}:{job}`) must not hit
     *  `/api/trello/cards/`. Shares the TanStack key with
     *  `<TrelloCardDetailSection>`. */
    external_id?: string | null
  }
  /** Optional Trello card detail fetcher — same callback shape as
   *  `KanbanApi.fetchTrelloCardDetail`. When provided AND the card
   *  is a Trello mirror (`provider === 'trello'` + `external_id`),
   *  the component fetches the detail and renders the comment
   *  thread inline.
   *
   *  Kept as a narrow callback (not the full `KanbanApi` type) so
   *  this component can be used inside isolated Storybook /
   *  test renders without dragging the whole API surface in. */
  fetchTrelloCardDetail?: (externalId: string) => Promise<TrelloCardDetail>
  /** Optional deep-link builder so the footer can offer "Open in
   *  Email Hub" even though we don't have an Email row yet. When
   *  omitted, the footer just shows the synthesis notice. */
  buildSearchHubUrl?: (sender: { name: string; addr: string }, subject: string) => string
}

/**
 * Read-only email-shaped view rendered from a Card's own metadata
 * (and optionally its Trello comments thread).
 *
 * Mirrors the chrome of `<EmailThreadView>` (header with subject /
 * from, scrollable body pane, footer) so the tab strip in
 * `<CardDetailDrawer>` feels uniform whether the card has a real
 * Email row or only the imported text.
 *
 * Body is rendered as `<pre>` whitespace-pre-wrap — the Trello
 * import doesn't preserve HTML, so the input is always plain text.
 * This is the safer default anyway: no iframe, no script-injection
 * surface, no `dangerouslySetInnerHTML`.
 */
export function EmailFromCard({
  card,
  fetchTrelloCardDetail,
  buildSearchHubUrl,
}: EmailFromCardProps) {
  const sender = parseEmailTitle(card.title)
  // Strip BOTH forward preambles (BenkoBoard block + "View in
  // Gmail/Outlook <url>" header); each is a no-op when its marker is
  // absent, so chaining is safe. The webmail deep link is surfaced
  // separately as a clickable "Open in …" (inert in the raw textarea).
  const body = stripWebmailPreamble(stripBenkoCommandPreamble(card.summary))
  const webmailLink = extractWebmailLink(card.summary)
  const receivedHint = card.updated_at ?? card.created_at ?? null

  // v3 (2026-05-20): fetch the rich Trello detail when available and
  // project its commentCard rows into an email thread. Shares the
  // cache key with `<TrelloCardDetailSection>` so the Card-tab pre-
  // fetch is reused. `enabled` gates it off (→ v2 placeholder view)
  // when the card isn't Trello-bridged or the host didn't wire it.
  const trelloEnabled =
    isTrelloMirrorCard(card) && !!fetchTrelloCardDetail
  const trelloQuery = useQuery<TrelloCardDetail>({
    queryKey: ['trello-card-detail', card.external_id],
    queryFn: () => fetchTrelloCardDetail!(card.external_id as string),
    enabled: trelloEnabled,
    staleTime: 30_000,
  })

  const thread = parseEmailThreadFromComments(trelloQuery.data?.comments ?? null)
  // Choose the header sender: when we have a parsed thread, use the
  // oldest sender as the "from" — that's the original email's
  // author. Otherwise fall back to the v2 title parse.
  const headerSender =
    thread.length > 0
      ? { name: thread[0].fromName, addr: thread[0].fromAddr }
      : sender

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="border-b border-neutral-200 px-4 py-2 dark:border-zinc-800">
        <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-neutral-500">
          <Mail className="h-3 w-3" /> Email
          <span className="ml-1 rounded-sm bg-amber-100 px-1 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
            {thread.length > 0
              ? `From Trello comments — ${thread.length} message${thread.length === 1 ? '' : 's'} (link to inbox pending)`
              : 'Imported from Trello — not yet linked to a synced inbox row'}
          </span>
        </div>
        <h3 className="mt-0.5 truncate text-sm font-semibold text-neutral-900 dark:text-neutral-100">
          {headerSender?.name ?? card.title}
        </h3>
        <div className="mt-1 truncate text-xs text-neutral-500 dark:text-zinc-400">
          {headerSender ? (
            <span className="font-medium text-neutral-700 dark:text-zinc-200">
              {headerSender.name} &lt;{headerSender.addr}&gt;
            </span>
          ) : (
            <span>(unknown sender)</span>
          )}
          {receivedHint && <> · imported {new Date(receivedHint).toLocaleString()}</>}
        </div>
      </header>

      <div className="flex-1 overflow-y-auto bg-neutral-50 dark:bg-zinc-950">
        <div className="space-y-3 p-3">
          {thread.length > 0 && (
            <ul
              data-testid="emailfromcard-thread"
              className="space-y-2"
              aria-label={`${thread.length} forwarded email${thread.length === 1 ? '' : 's'} from Trello comments`}
            >
              {thread.map((m) => (
                <li
                  key={m.id}
                  className="rounded border border-neutral-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-900"
                >
                  <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 text-[11px]">
                    <span className="font-semibold text-neutral-800 dark:text-neutral-100">
                      {m.fromName}{' '}
                      <span className="font-normal text-neutral-500 dark:text-zinc-400">
                        &lt;{m.fromAddr}&gt;
                      </span>
                    </span>
                    {m.date && (
                      <time
                        dateTime={m.date}
                        className="tabular-nums text-[10.5px] text-neutral-500 dark:text-zinc-400"
                      >
                        {new Date(m.date).toLocaleString()}
                      </time>
                    )}
                  </div>
                  <pre className="whitespace-pre-wrap break-words text-[13px] leading-relaxed text-neutral-800 dark:text-neutral-200">
                    {m.body}
                  </pre>
                </li>
              ))}
            </ul>
          )}

          {/* v2 title/summary fallback — shown WHEN we have no
              email-shaped Trello comments. We keep the v2 view
              so cards without a Trello bridge (manual cards that
              just happen to have an email-shaped title) still
              render usefully. */}
          {thread.length === 0 && body && (
            <pre className="whitespace-pre-wrap break-words rounded border border-neutral-200 bg-white p-3 text-[13px] leading-relaxed text-neutral-800 dark:border-zinc-800 dark:bg-zinc-900 dark:text-neutral-200">
              {body}
            </pre>
          )}

          {thread.length === 0 && !body && trelloQuery.isFetching && (
            <div className="flex items-center justify-center gap-2 py-8 text-[12px] text-neutral-500 dark:text-zinc-400">
              <Mail className="h-4 w-4 animate-pulse opacity-50" aria-hidden />
              Loading Trello comments…
            </div>
          )}

          {thread.length === 0 && !body && !trelloQuery.isFetching && (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-neutral-500 dark:text-zinc-400">
              <Mail className="h-8 w-8 opacity-40" aria-hidden />
              <p className="text-sm font-medium">
                Email body wasn&apos;t imported with this card
              </p>
              <p className="max-w-[280px] text-[11.5px] leading-snug">
                The card retains the sender header from Trello but the body content
                is missing. Open the Email Hub and search for the sender to find
                the original message.
              </p>
            </div>
          )}
        </div>
      </div>

      <footer className="flex items-center justify-between gap-3 border-t border-neutral-200 px-3 py-1.5 dark:border-zinc-800">
        <span className="text-[10.5px] text-neutral-400">
          {thread.length > 0
            ? 'Rendered from Trello comments — link to canonical inbox row is still pending.'
            : 'Synthesised from card title + description — not a live mailbox view.'}
        </span>
        <div className="flex shrink-0 items-center gap-1.5">
          {webmailLink && (
            <a
              href={webmailLink.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="emailfromcard-webmail-link"
              className="inline-flex items-center gap-1 rounded bg-blue-50 px-2 py-1 text-[11px] font-medium text-blue-700 hover:bg-blue-100 dark:bg-blue-950/40 dark:text-blue-300 dark:hover:bg-blue-950/60"
            >
              <ExternalLink className="h-3 w-3" /> Open in {webmailLink.provider}
            </a>
          )}
          {headerSender && buildSearchHubUrl && (
            <a
              href={buildSearchHubUrl(headerSender, card.title)}
              className="inline-flex items-center gap-1 rounded bg-neutral-100 px-2 py-1 text-[11px] font-medium text-neutral-700 hover:bg-neutral-200 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700"
            >
              Find in Email Hub →
            </a>
          )}
        </div>
      </footer>
    </div>
  )
}
